/* eslint-disable max-len */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromPlayer from './player.reducer';
import { LocationStatus, PlayerStatus } from './player.reducer';

const getPlayerFeatureState = createFeatureSelector<fromPlayer.PlayerState>('player');

export const isOnline = createSelector(getPlayerFeatureState, (state: fromPlayer.PlayerState) => state && state.isOnline);

export const isInit = createSelector(getPlayerFeatureState, (state: fromPlayer.PlayerState) => state && state.isInit);

export const areInitialTracksLoaded = createSelector(
  getPlayerFeatureState,
  (state: fromPlayer.PlayerState) => state && state.initialTracksLoaded,
);

export const selectLocation = createSelector(getPlayerFeatureState, (state: fromPlayer.PlayerState) => state && state.location);

export const selectLocationGuid = createSelector(selectLocation, location => location && location.guid);

export const selectMinSecondsCacheExpected = createSelector(selectLocation, location => location && location.minSecondsCacheExpected);

export const selectBufferSecondsCacheExpected = createSelector(selectLocation, location => location && location.bufferSecondsCacheExpected);

export const selectInterruptions = createSelector(selectLocation, location => location && location.interruptions);

export const selectPlayerStatus = createSelector(getPlayerFeatureState, (state: fromPlayer.PlayerState) => state && state.status);

export const selectPlayerVolume = createSelector(getPlayerFeatureState, (state: fromPlayer.PlayerState) => state && state.volume);

export const selectGeneralStatus = createSelector(getPlayerFeatureState, state => state.status === PlayerStatus.Ready ? (state.muted ? 'Stopped' : 'Playing track') : state.status);

export const isPlayerRefreshing = createSelector(selectPlayerStatus, (status: PlayerStatus) => status === PlayerStatus.Refreshing);

export const selectPlayerStatusMessage = createSelector(selectPlayerStatus, (status: fromPlayer.PlayerStatus) => {
  switch (status) {
    case PlayerStatus.Ready:
      return 'Player is ready';
    case PlayerStatus.PlayingSource:
      return 'Player is playing';
    case PlayerStatus.PlayingPrayerTime:
      return 'Player is playing a prayer interruption';
    case PlayerStatus.NotReady:
    default:
      return 'Loading player...';
  }
});

export const isLocationLocked = createSelector(
  getPlayerFeatureState,
  (state: fromPlayer.PlayerState) => state && state.locationStatus === LocationStatus.Locked,
);

export const isMuted = createSelector(getPlayerFeatureState, (state: fromPlayer.PlayerState) => state && state.status === PlayerStatus.PlayingPrayerTime ? true : state && state.muted);

export const isInterruptionActive = createSelector(
  getPlayerFeatureState,
  (state: fromPlayer.PlayerState) => (state && state.status === PlayerStatus.PlayingPrayerTime) || state.status === PlayerStatus.PlayingAd,
);

export const isPrayerTimerActive = createSelector(
  getPlayerFeatureState,
  (state: fromPlayer.PlayerState) => state && state.status === PlayerStatus.PlayingPrayerTime,
);

export const isAdActive = createSelector(getPlayerFeatureState, state => state && state.status === PlayerStatus.PlayingAd);

export const selectCurrentPlaylist = createSelector(
  getPlayerFeatureState,
  (state: fromPlayer.PlayerState) => state && state.currentPlaylist,
);

export const selectCurrentPlaylistGuid = createSelector(selectCurrentPlaylist, playlist => playlist && playlist.guid);

export const selectMaxSecondsElapsedTimeDiffAllowed = createSelector(
  selectCurrentPlaylist,
  playlist => playlist && playlist.maxSecondsElapsedTimeDiffAllowed,
);

export const selectTracks = createSelector(getPlayerFeatureState, (state: fromPlayer.PlayerState) => state && state.tracks);

export const selectLastTrack = createSelector(
  getPlayerFeatureState,
  (state: fromPlayer.PlayerState) => state && state.tracks && state.tracks[state.tracks.length - 1],
);

export const selectActiveTrack = createSelector(getPlayerFeatureState, (state: fromPlayer.PlayerState) => state && state.activeTrack);

export const isActiveTrackSilence = createSelector(
  selectActiveTrack,
  track => track && (track.type.toLowerCase() === 'silence' || track.metadata.title.toLowerCase().startsWith('silence -')),
);

export const selectLocationPlaylist = createSelector(selectPlayerStatus, selectLocation, (status, location) => status === PlayerStatus.PlayingPrayerTime || !location || !location.playlist ? '' : location.playlist);

export const selectLocationPlaylistName = createSelector(selectLocationPlaylist, playlist => playlist && playlist.name);

export const selectActiveTrackMetadata = createSelector(selectActiveTrack, track => track && track.metadata);

export const selectCurrentSongName = createSelector(selectPlayerStatus, selectActiveTrackMetadata, (status, metadata) => status === PlayerStatus.PlayingPrayerTime || !metadata || !metadata.title
    ? ''
    : metadata.title.toLocaleLowerCase().startsWith('silence -')
    ? 'Silence'
    : metadata.title);

export const selectCurrentSongArtistName = createSelector(selectPlayerStatus, selectActiveTrackMetadata, (status, metadata) => status === PlayerStatus.PlayingPrayerTime || !metadata || !metadata.artist ? '' : metadata.artist);

export const selectTrackErrors = createSelector(getPlayerFeatureState, (state: fromPlayer.PlayerState) => state && state.trackErrors);

export const selectNumTrackErrors = createSelector(selectTrackErrors, trackErrors => trackErrors && trackErrors.length);

export const isForcePlayActive = createSelector(getPlayerFeatureState, state => state.forcePlayNextTrack);

export const isControlVisible = createSelector(
  isPrayerTimerActive,
  isActiveTrackSilence,
  isAdActive,
  (prayerTime, isSilence, isAd) => !prayerTime && !isSilence && !isAd,
);
